import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';

type Severity = 'critical' | 'warning' | 'info';

interface SeverityChipProps {
  severity: Severity;
  label: string;
}

/**
 * SeverityChip — filled chip with severity-based background tint.
 * Uses a subtle alpha fill instead of outline for better visual weight.
 */
export default function SeverityChip({ severity, label }: SeverityChipProps) {
  const theme = useTheme();

  const colorMap: Record<Severity, string> = {
    critical: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  const dotColor = colorMap[severity];

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: alpha(dotColor, 0.12),
        color: dotColor,
        fontWeight: 600,
        fontSize: '0.7rem',
        border: `1px solid ${alpha(dotColor, 0.2)}`,
        '& .MuiChip-label': { px: 1 },
        '&::before': {
          content: '""',
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: dotColor,
          ml: 0.75,
          mr: -0.25,
          boxShadow: `0 0 6px ${alpha(dotColor, 0.5)}`,
        },
      }}
    />
  );
}
